import {
  camelCase,
  forEach,
  isArray,
  isObject,
  isPlainObject,
  isString,
  snakeCase,
} from "lodash";

import { signOut } from "@src/auth/utils";
import { bugsnagClient } from "../../bugsnag";
import { isDev, isProd } from "../../env";

import { CamelCasedPropertiesDeep, SnakeCasedPropertiesDeep } from "type-fest";
export const months = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
] as const;

export const monthsFullName = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

export const convertNumericDate = (date: string): string => {
  if (!date.includes("-")) return date;
  const yearMonthArr = date.split("-");
  return (
    monthsFullName[parseInt(yearMonthArr[1]!) - 1] + ", " + yearMonthArr[0]!
  );
};

export const camelCaseCipher = <T>(
  key: string,
  value: T,
  options?: any
): {
  [key: string]: T;
} => ({
  [camelCase(key)]: value,
});

export const snakeCaseCipher = <T>(
  key: string,
  value: T
): {
  [key: string]: T;
} => ({
  [snakeCase(key)]: value,
});

type Options = {
  allowNulls?: boolean;
  allowUnknownKeys?: boolean;
};

// Resolves a single [key, value] entry into the partial object that should be
// merged into the formatted output. Extracted from formatDataObj to keep the
// branching flat.
const resolveFormattedEntry = (
  key: string,
  value: any,
  inputObj: any,
  cipher: any,
  options: Options
): Record<string, any> => {
  const { allowNulls = false, allowUnknownKeys = false } = options;

  if (typeof cipher === "function") return cipher(key, value, inputObj) || {};
  if (typeof cipher[key] === "function") {
    return cipher[key](key, value, inputObj) || {};
  }
  if (value != null && typeof value === "object" && !Array.isArray(value)) {
    return formatDataObj(value, cipher[key], options);
  }
  if (allowNulls || value != null) {
    let formattedKey = cipher[key];
    if (!formattedKey && allowUnknownKeys) formattedKey = key;
    if (formattedKey) return { [formattedKey]: value };
  }
  return {};
};

export const formatDataObj = (
  // @ts-expect-error - TS7006 - Parameter 'inputObj' implicitly has an 'any' type.
  inputObj,
  cipher: any,
  options: Options = {}
): any => {
  // This function will flatten objects nested due to foreign key constraints
  if (!cipher) return {};

  const formattedOutput: Record<string, any> = {};
  for (const [key, value] of Object.entries(inputObj)) {
    Object.assign(
      formattedOutput,
      resolveFormattedEntry(key, value, inputObj, cipher, options)
    );
  }
  return formattedOutput;
};

// @ts-expect-error - TS7006 - Parameter 'inputArr' implicitly has an 'any' type.
export const formatDataArray: any = (inputArr, cipher: any, options: Options) =>
  // @ts-expect-error - TS7006 - Parameter 'curr' implicitly has an 'any' type.
  inputArr.map((curr) => formatDataObj(curr, cipher, options));

// NOTE - this function specifically converts utility VALUES to camelCase as well as keys. The reverse will NOT be true by default when converting back to snake_case.
export const recursiveCamelCaseCipher = (
  inboundObject: Array<Record<any, any>> | Record<any, any>
) => {
  if (inboundObject == null) return null;
  const camelCaseObject: any = isArray(inboundObject) ? [] : {};
  forEach(inboundObject, function (value, key) {
    let newValue;
    if (isPlainObject(value)) {
      // checks that a value is a plain object - for recursive key conversion
      newValue = recursiveCamelCaseCipher(value); // recursively update keys of any values that are also objects
    } else if (isArray(value)) {
      newValue = value.map((val) =>
        isObject(val) ? recursiveCamelCaseCipher(val) : val
      ); // recursively update keys of any values that are also objects
    } else if (
      isString(value) &&
      (key === "utility" || key === "utility_type")
    ) {
      newValue = camelCase(value);
    } else {
      newValue = value;
    }

    let newKey = key;
    const keyWithoutNumbers = key.toString().replace(/\d/g, ""); // remove numbers b.c snakeCase will add _ between letters and numbers (i.e. hs3)
    const snakeCaseKey = snakeCase(keyWithoutNumbers);
    if (
      snakeCaseKey === keyWithoutNumbers ||
      `${snakeCaseKey}_` === keyWithoutNumbers // needed so keys like emissions_scope_3 will get camelCase'd
    ) {
      newKey = camelCase(key); // only change snakeCase keys
    }
    camelCaseObject[newKey] = newValue;
  });
  return camelCaseObject;
};

export const recursiveCamelCaseCipherTypesafe = <
  T extends Record<string, any> | Array<Record<string, any>>
>(
  inboundObject: T
): T extends any[]
  ? CamelCasedPropertiesDeep<T[number]>[]
  : CamelCasedPropertiesDeep<T> => {
  return recursiveCamelCaseCipher(inboundObject);
};

export const recursiveSnakeCaseCipher = (
  inboundObject: Array<any> | Record<any, any>,
  options?:
    | {
        convertUtilityValues?: boolean;
      }
    | number // allow number for usage inline inside .maps etc
) => {
  if (inboundObject == null) return null;
  const snakeCaseObject: any = isArray(inboundObject) ? [] : {};
  forEach(inboundObject, function (value, key) {
    let newValue;
    if (isPlainObject(value)) {
      // checks that a value is a plain object - for recursive key conversion
      newValue = recursiveSnakeCaseCipher(value); // recursively update keys of any values that are also objects
    } else if (isArray(value)) {
      newValue = value.map((val) =>
        isObject(val) ? recursiveSnakeCaseCipher(val) : val
      ); // recursively update keys of any values that are also objects
    } else {
      newValue = value;
    }

    let newKey = key;
    const keyWithoutNumbers = key.toString().replace(/\d/g, ""); // remove numbers b.c camelCase will capitalize letters after a number
    if (camelCase(keyWithoutNumbers) === keyWithoutNumbers)
      newKey = snakeCase(key); // only change camelCase keys

    if (
      typeof options === "object" &&
      options?.convertUtilityValues &&
      isString(value) &&
      (key === "utility" || key === "utilityType")
    ) {
      snakeCaseObject[newKey] = snakeCase(value);
    } else {
      snakeCaseObject[newKey] = newValue;
    }
  });
  return snakeCaseObject;
};
export const recursiveSnakeCaseCipherTypesafe = <
  T extends Record<string, any> | Array<Record<string, any>>
>(
  inboundObject: T
): T extends any[]
  ? SnakeCasedPropertiesDeep<T[number]>[]
  : SnakeCasedPropertiesDeep<T> => {
  return recursiveSnakeCaseCipher(inboundObject);
};

export const logoutOnError = async (e: any) => {
  if (
    e?.status === 401 &&
    e?.response?.body?.message === "JWSError JWSInvalidSignature"
  ) {
    await signOut();
    return true;
  }
};

export const bugsnagPostgrestErrorHandler = async (e: any): Promise<void> => {
  if (await logoutOnError(e)) return;

  if (isDev) {
    console.log(e); // eslint-disable-line no-console
  } else if (isProd) {
    const metaData = e?.response?.req
      ? {
          type: "Postgrest Request Failed",
          method: e.response.req.method,
          url: e.response.req.url,
          message: e.response.body?.message,
          req_data: JSON.stringify(e.response.req._data),
        }
      : {
          type: "Postgrest Request Processing Failed",
        };
    bugsnagClient?.leaveBreadcrumb("Postgrest Request Failed", metaData);
    bugsnagClient?.notify(e, (ev) => {
      ev.severity = "warning";
      ev.addMetadata("error", metaData);
    });
  }
};

export const bugsnagPortfolioManagerErrorHandler = (e: any) => {
  if (isDev) {
    console.log(e); // eslint-disable-line
  } else if (isProd) {
    const metaData = e?.response?.req
      ? {
          type: "Portfolio Manager Request Failed",
          method: e.response.req.method,
          url: e.response.req.url,
          message: e.response.text,
          req_data: JSON.stringify(e.response.req._data),
        }
      : {
          type: "Portfolio Manager Request Processing Failed",
        };
    bugsnagClient?.leaveBreadcrumb(
      "Portfolio Manager Request Failed",
      metaData
    );
    bugsnagClient?.notify(e, (ev) => {
      ev.severity = "warning";
      ev.addMetadata("error", metaData);
    });
  }
};

export const bugsnagGeneralErrorHandler = (e: any) => {
  if (isDev) {
    console.log(e); // eslint-disable-line no-console
  } else if (isProd) {
    bugsnagClient?.notify(e);
  }
};

export const raise = (errMessage: string): never => {
  throw new Error(errMessage);
};

export const getUserImageUrl = (imageKey?: string | null) => {
  if (!imageKey) return null;
  return `https://bractlet-public${
    isDev ? "-dev" : ""
  }.s3.amazonaws.com/intel/user_images/${imageKey}.jpeg`;
};
